import React, { useState, useEffect } from 'react';
import { apiClient } from '../../../api/apiClient';
import { GET_SONG_DISTRIBUTIONS_URL } from '../../../api/song';
import EmptyState from '../../../components/shared/EmptyState';

// Distribution tab component
function DistributionTab({ song }) {
  const [distributions, setDistributions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!song?.id) return;

    const fetchDistributions = async () => {
      setIsLoading(true);
      try {
        const data = await apiClient(GET_SONG_DISTRIBUTIONS_URL(song.id));
        if (data && data.distributions) {
          setDistributions(data.distributions);
        }
      } catch (err) {
        console.warn('Failed to load song distributions:', err);
        setDistributions([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDistributions();
  }, [song?.id]);

  // ── Skeleton card (mirrors the real card layout) ──────────────────────────
  const SkeletonCard = () => (
    <div className="distribution-account-card" style={{ opacity: 0.6 }}>
      <div className="card-header-row">
        <div style={{ width: '60%', height: 14, background: '#e2e8f0', borderRadius: 4 }} />
        <div style={{ width: 60, height: 20, background: '#e2e8f0', borderRadius: 10 }} />
      </div>
      <div className="account-details-rows">
        {[1, 2, 3].map((i) => (
          <div key={i} className="account-detail-row">
            <div className="detail-icon-wrapper" style={{ background: '#f1f5f9' }} />
            <div className="detail-text">
              <div style={{ width: '40%', height: 10, background: '#e2e8f0', borderRadius: 3, marginBottom: 6 }} />
              <div style={{ width: '70%', height: 12, background: '#e2e8f0', borderRadius: 3 }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="distribution-tab-container">
      {/* Header */}
      <div className="tab-section-header">
        <div className="tab-header-left">
          <div className="tab-header-icon-blue">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z" />
              <path d="m9 12 2 2 4-4" />
            </svg>
          </div>
          <h3 className="section-title main-section-title">Distribution</h3>
        </div>
      </div>

      {/* Account Columns */}
      <div className="distribution-accounts-grid">
        {isLoading ? (
          // Skeleton placeholders while loading
          <>
            <SkeletonCard />
            <SkeletonCard />
          </>
        ) : distributions.length === 0 ? (
          // Empty state — no distributors linked to this song
          <div style={{ gridColumn: '1 / -1' }}>
            <EmptyState
              title="No distributors linked"
              subtitle="This song has not been assigned to any distribution platform yet."
              hasSearch={false}
            />
          </div>
        ) : (
          distributions.map((dist) => (
            <div key={dist.songDistributorId} className="distribution-account-card">
              <div className="card-header-row">
                <h4 className="account-card-title">
                  {dist.isActive ? 'Current account' : 'Previous account'}
                </h4>
                <span className={`status-badge ${dist.isActive ? 'active-badge' : 'disabled-badge'}`}>
                  {dist.isActive ? 'Active' : 'Disabled'}
                </span>
              </div>

              <div className="account-details-rows">
                {/* Distribution Platform */}
                <div className="account-detail-row">
                  <div className="detail-icon-wrapper">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="2" y1="12" x2="22" y2="12" />
                      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                    </svg>
                  </div>
                  <div className="detail-text">
                    <span className="detail-label">Distribution Platform</span>
                    <span className="detail-value">{dist.company || '—'}</span>
                  </div>
                </div>

                {/* Distribution Account (email) */}
                <div className="account-detail-row">
                  <div className="detail-icon-wrapper">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                      <circle cx="12" cy="7" r="4" />
                    </svg>
                  </div>
                  <div className="detail-text">
                    <span className="detail-label">Distribution account</span>
                    <span className="detail-value">{dist.email || '—'}</span>
                  </div>
                </div>

                {/* Date */}
                <div className="account-detail-row">
                  <div className="detail-icon-wrapper">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                      <line x1="16" y1="2" x2="16" y2="6" />
                      <line x1="8" y1="2" x2="8" y2="6" />
                      <line x1="3" y1="10" x2="21" y2="10" />
                    </svg>
                  </div>
                  <div className="detail-text">
                    <span className="detail-label">{dist.isActive ? 'Activated On' : 'Change By'}</span>
                    <span className="detail-value">
                      {(() => {
                        if (!dist.changedAt) return '—';
                        const dateObj = new Date(dist.changedAt);
                        if (isNaN(dateObj.getTime())) return dist.changedAt;

                        const today = new Date();
                        const isToday = dateObj.getFullYear() === today.getFullYear() &&
                          dateObj.getMonth() === today.getMonth() &&
                          dateObj.getDate() === today.getDate();

                        if (dist.isActive && isToday) {
                          return 'Today';
                        }

                        const yyyy = dateObj.getFullYear();
                        const mm = String(dateObj.getMonth() + 1).padStart(2, '0');
                        const dd = String(dateObj.getDate()).padStart(2, '0');
                        return `${yyyy}.${mm}.${dd}`;
                      })()}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default DistributionTab;
